import { createApi } from '@reduxjs/toolkit/dist/query/react'
import config from '../config'
import { staggeredBaseQueryWithBailOut } from '../libs/services'
import {
	ExamTemplateDiff,
	ExamTemplate,
	ExamTemplateWithId,
} from '../model/exam'
import { Optional } from '../utils/utilityTypes'
import { useTeloSelector } from '../store'
import {
	selectCurrentPractice,
	selectPracticeByStoreId,
} from '../features/practices/selectors'
import { useTeloRouter } from '../routing/teloRouter'
import { selectExam } from '../features/exams/selectors'
import { ReadTeloParamsFn } from '../routing/teloRouterUtils'

const APPOINTMENT_TYPES = 'appointment-types' as const
const TAG = 'exam-templates' as const
const ALL_TEMPLATES_ID = 'ALL_TEMPLATES' as const
const BASE_URL = `/exam-templates`

type QueryArgs = Optional<{
	appointmentTypeCode?: string
	legalEntityId?: string
	remote?: boolean
	filterByUsername?: boolean
	role?: string
}>

export const examTemplateApi = createApi({
	reducerPath: `examTemplateApi`,
	tagTypes: [TAG, APPOINTMENT_TYPES],
	baseQuery: staggeredBaseQueryWithBailOut(config.apiUrlV3),
	endpoints: builder => ({
		getTemplatesBy: builder.query<ExamTemplateWithId[], QueryArgs>({
			query: args => {
				const appointmentTypeCode = args?.appointmentTypeCode
				const legalEntityId = args?.legalEntityId
				const remote = args?.remote
				const filterByUsername = args?.filterByUsername
				const role = args?.role

				const qParams: Record<string, string | string> = {}
				if (appointmentTypeCode) {
					qParams['appointmentTypeCode'] = appointmentTypeCode
				}
				if (legalEntityId) {
					qParams['legalEntityId'] = legalEntityId
				}
				if (filterByUsername) {
					qParams['filterByUsername'] = `${!!filterByUsername}`
				}
				if (remote != null) {
					qParams['remote'] = `${!!remote}`
				}
				if (role) {
					qParams['role'] = role
				}
				const query = new URLSearchParams(qParams)

				const anyArg =
					remote != null ||
					!!legalEntityId ||
					!!appointmentTypeCode ||
					!!role ||
					!!filterByUsername

				return {
					url: anyArg ? `${BASE_URL}?${query.toString()}` : `${BASE_URL}`,
					method: 'GET',
				}
			},
			providesTags: tagAll,
		}),
		doesTemplateNameExist: builder.query<
			boolean,
			{ templateName: string; username: string }
		>({
			query: args => {
				if (!args.templateName) {
					throw new Error('Cannot search an empty template name')
				}
				if (!args.username) {
					throw new Error('Cannot search without username')
				}
				const query = new URLSearchParams(args)
				return {
					url: `${BASE_URL}/exist?${query.toString()}`,
					method: 'GET',
				}
			},
			forceRefetch: () => true,
		}),
		addExamTemplate: builder.mutation<void, { template: ExamTemplate }>({
			query: args => ({
				url: BASE_URL,
				body: args,
				method: 'POST',
			}),
			invalidatesTags: invalidateAllTags(),
		}),
		deleteExamTemplates: builder.mutation<void, { ids: ReadonlyArray<string> }>(
			{
				query: args => ({
					url: BASE_URL,
					body: args,
					method: 'PATCH',
				}),
				invalidatesTags: invalidateAllTags(),
			},
		),
		toggleIsDefault: builder.mutation<
			ExamTemplateWithId,
			{
				id: string
				newIsDefault: boolean
				args: QueryArgs
			}
		>({
			query: ({ id, newIsDefault }) => {
				return {
					url: `${BASE_URL}/${id}/is-default`,
					method: 'PUT',
					body: { isDefault: newIsDefault },
				}
			},
			onQueryStarted: async (
				{ id, newIsDefault, args },
				{ dispatch, queryFulfilled },
			) => {
				const patchResult = dispatch(
					optimisticUpdate({
						id,
						args,
						update: draft => {
							draft.metadata.isDefault = newIsDefault
						},
					}),
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
			invalidatesTags: (result, error, arg) => [{ type: TAG, id: arg.id }],
		}),
		toggleIsActive: builder.mutation<
			ExamTemplateWithId,
			{
				id: string
				newIsActive: boolean
				args: QueryArgs
			}
		>({
			query: ({ id, newIsActive }) => {
				return {
					url: `${BASE_URL}/${id}/is-active`,
					method: 'PUT',
					body: { isActive: newIsActive },
				}
			},
			invalidatesTags: (result, error, arg) => [{ type: TAG, id: arg.id }],
		}),
		updateExamTemplate: builder.mutation<
			ExamTemplateWithId,
			{
				id: string
				edited: ExamTemplateDiff
			}
		>({
			query: ({ id, edited }) => {
				return {
					url: `${BASE_URL}/${id}`,
					method: 'PATCH',
					body: { data: edited },
				}
			},
			invalidatesTags: (result, error, arg) => [{ type: TAG, id: arg.id }],
		}),
		getAppointmentTypesBy: builder.query<ReadonlyArray<string>, string>({
			query: legalEntityId => {
				if (!legalEntityId) {
					throw new Error('Cannot search an empty template name')
				}
				return {
					url: `${BASE_URL}/appointment-types?${new URLSearchParams({
						legalEntityId,
					}).toString()}`,
					method: 'GET',
				}
			},
		}),
	}),
})

function invalidateAllTags() {
	return [{ type: TAG, id: ALL_TEMPLATES_ID }]
}

function tagAll(result: ExamTemplateWithId[] | undefined) {
	return result
		? [
				...result.map(({ _id }) => ({ type: TAG, id: _id })),
				{ type: TAG, id: ALL_TEMPLATES_ID },
		  ]
		: [{ type: TAG, id: ALL_TEMPLATES_ID }]
}

const optimisticUpdate = ({
	update,
	id,
	args,
}: {
	id: string
	update: (draft: ExamTemplateWithId) => void
	args: QueryArgs
}) => {
	return examTemplateApi.util.updateQueryData(
		'getTemplatesBy',
		args,
		drafts => {
			const editedDraft = drafts.find(d => d._id === id)
			if (editedDraft) {
				update(editedDraft)
			}
		},
	)
}

type FlowSection = 'ExamTypeConfiguration' | 'PracticeConfiguration' | 'Exam'

const practiceConfigurationId = (readRouteParams: ReadTeloParamsFn) => {
	const { practiceId } = readRouteParams({
		name: 'practiceId',
		required: false,
	})
	return practiceId || undefined
}

const getExamId = (readRouteParams: ReadTeloParamsFn) => {
	const { examId } = readRouteParams({
		name: 'examId',
		required: false,
	})
	return examId || ''
}

export const useGetTemplatesByQuery = (type: FlowSection) => {
	let filter: QueryArgs = {}
	let skipQuery: boolean = false
	const { readRouteParams } = useTeloRouter()

	const examId = getExamId(readRouteParams)
	const exam = useTeloSelector(selectExam(examId))
	const currentPractice = useTeloSelector(selectCurrentPractice)
	const examPractice = useTeloSelector(
		selectPracticeByStoreId(exam?.store._id || ''),
	)

	switch (type) {
		case 'ExamTypeConfiguration':
			filter = null
			break
		case 'PracticeConfiguration': {
			const practiceToEditId = practiceConfigurationId(readRouteParams)
			filter = {
				legalEntityId: practiceToEditId,
				role: 'GlobalAdmin', // Doctors with LEA privileges and Global Admins are the same thing
			}
			skipQuery = !practiceToEditId
			break
		}
		case 'Exam':
			const selectedLegalEntityId = currentPractice?._id || examPractice?._id
			filter = {
				appointmentTypeCode: exam?.examType,
				legalEntityId: selectedLegalEntityId,
				remote: exam?.mode === 'remote' ? true : false,
			}
			skipQuery = !exam || !selectedLegalEntityId || !exam.examType
			break
	}

	return examTemplateApi.useGetTemplatesByQuery(filter, {
		skip: skipQuery,
	})
}

export const {
	useAddExamTemplateMutation,
	useDeleteExamTemplatesMutation,
	useUpdateExamTemplateMutation,
	useDoesTemplateNameExistQuery,
	useToggleIsActiveMutation,
	useToggleIsDefaultMutation,
	useGetAppointmentTypesByQuery,
} = examTemplateApi
